<script setup lang="ts">
import { Fancybox } from "@fancyapps/ui";
import {find} from 'lodash';
import {ChevronLeftIcon, ChevronRightIcon } from "lucide-vue-next";
import {ref} from "vue";
import type {ProductImageResponse} from "../../../responses/ProductImageResponse";
import type {ProductResponse} from "../../../responses/ProductResponse";
import type {ProductVariantResponse} from "../../../responses/ProductVariantResponse";
import {useProductStore} from "../../stores/product";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import NoImageURL from "../../../../images/no-image.png";
import VideoWrapper from "../VideoWrapper.vue";

interface Props{
    product: ProductResponse
}
const props = defineProps<Props>();
const selectedImage = ref(props.product.images[0]);
const productStore = useProductStore();

productStore.events.on('selectedVariantChanged', async (variant: ProductVariantResponse) => {
    const variantImage = find(props.product.images, (productImage: ProductImageResponse)=> productImage.product_variant_id === variant?.id);
    selectedImage.value = variantImage ?? props.product.images[0];
});
Fancybox.bind("[data-fancybox]", {
    Thumbs: false,
    videoAutoplay: false,
});
</script>
<template>
    <div class="w-full" v-if="product.images.length">
        <a :href="image.video_id !== null ? image.video_embed_url : image.image_url" :data-type="image.video_id !== null ? 'iframe' : 'image'" v-for="image in product.images" :class="{'hidden': image.id !== selectedImage.id}" data-fancybox="gallery">
            <video-wrapper v-if="selectedImage?.video_id !== null" class="w-full lg:block" :video-id="selectedImage.video_id" :source="selectedImage.video_service"></video-wrapper>
            <img v-else :alt="selectedImage.alt_text ?? product.name" :src="selectedImage.image_url" class="h-auto w-full object-contain" />
        </a>

        <div class="mt-4 flex items-center justify-center gap-5">
            <button type="button"
                class="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full text-secondary-300 transition hover:bg-secondary-50 hover:text-secondary-500 md:inline-flex"
                aria-label="Vorige afbeelding">
                <ChevronLeftIcon class="h-5 w-5" />
            </button>

            <div class="flex max-w-full w-full gap-4">
                <button
                    v-for="(image, index) in product.images"
                    :key="image"
                    type="button"
                    class="basis-1/3 shrink-1 overflow-hidden transition hover:opacity-100 md:basis-1/3 md:shrink"
                    :class="{'opacity-70': image.id !== selectedImage.id, 'opacity-100': image.id === selectedImage.id}"
                    @click="selectedImage = image"
                >
                    <img
                        :src="image.image_url"
                        :alt="image.alt_text ?? product.name"
                        class="h-auto w-full object-contain"
                    />
                </button>
            </div>

            <button type="button"
                class="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full text-secondary-300 transition hover:bg-secondary-50 hover:text-secondary-500 md:inline-flex"
                aria-label="Volgende afbeelding">
                <ChevronRightIcon class="h-5 w-5" />
            </button>
        </div>
    </div>
    <div v-else>
        <div class="rounded-xl overflow-hidden relative w-fit">
            <img :alt="product.name" :src="product.thumbnail?.image_path ?? NoImageURL" data-fancybox="gallery" />
        </div>
    </div>
</template>
